import { type ClassValue, clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import type { Beneficiary } from '@/models';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * `dd-MM-yyyy` is the app-wide date format — it matches what the native date inputs display,
 * so a value never reads one way in a field and another way in a table beside it.
 */
export const DATE_FORMAT = 'dd-MM-yyyy';

export function formatDate(iso: string | null | undefined, pattern = DATE_FORMAT): string {
  if (!iso) return '--';
  try {
    return format(parseISO(iso), pattern);
  } catch {
    return '--';
  }
}

/**
 * Today as the `yyyy-MM-dd` value an `<input type="date">` expects, in local time.
 * Not `toISOString()`, which reports the UTC day — that is yesterday for the first 5½ hours of
 * every IST day.
 */
export function todayInputDate(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function calculateAge(dob: string | null | undefined): number {
  if (!dob) return 0;
  try {
    const birthDate = parseISO(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch {
    return 0;
  }
}

export function formatDateTime(iso: string | null | undefined): string {
  return formatDate(iso, `${DATE_FORMAT} hh:mm a`);
}

const IST_TIME_ZONE = 'Asia/Kolkata';

// Backend sometimes omits the timezone designator on datetimes that are actually UTC
// (e.g. "2026-07-15T16:25:08.296254"). Treat any offset-less string as UTC before converting.
function parseAsUtc(iso: string): Date {
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTimezone ? iso : `${iso}Z`);
}

const IST_DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

/**
 * Intl has no way to ask for a dash separator, and every day-first locale uses slashes, so the
 * separator is swapped afterwards. Only the date part contains slashes — the time uses colons —
 * so this is safe for the datetime variant too.
 */
export function formatDateIST(iso: string | null | undefined, options: Intl.DateTimeFormatOptions = IST_DATE_OPTIONS): string {
  if (!iso) return '--';
  const date = parseAsUtc(iso);
  if (Number.isNaN(date.getTime())) return '--';
  // en-GB rather than en-IN: both are day-first, but en-GB keeps the numeric parts padded.
  return new Intl.DateTimeFormat('en-GB', { timeZone: IST_TIME_ZONE, ...options })
    .format(date)
    .replace(/\//g, '-');
}

export function formatDateTimeIST(iso: string | null | undefined): string {
  return formatDateIST(iso, { ...IST_DATE_OPTIONS, hour: '2-digit', minute: '2-digit', hour12: true });
}

// Converts a plain "YYYY-MM-DD" <input type="date"> value to a UTC ISO string for the backend.
// Date-only strings are parsed as UTC midnight per spec, so no timezone shift happens here.
export function toUtcIsoDate(dateOnly: string | null | undefined): string | undefined {
  if (!dateOnly) return undefined;
  const date = new Date(dateOnly);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value);
}

export function percent(part: number, whole: number): number {
  if (whole === 0) return 0;
  return Math.round((part / whole) * 100);
}

export function initials(name: string | undefined | null): string {
  return (name ?? '')
    .split(' ')
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Downloads rows as a .xlsx workbook. Keys of the first row become the header,
 * matching how the table columns are shaped by each caller.
 *
 * SheetJS is imported on demand: it is a large dependency and an export is a rare,
 * deliberate click, so it should not sit in the initial bundle.
 */
export async function exportToExcel<T extends Record<string, unknown>>(
  filename: string,
  rows: T[],
  sheetName = 'Sheet1',
): Promise<void> {
  if (rows.length === 0) return;
  const XLSX = await import('xlsx');
  const sheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  // Excel rejects sheet names over 31 chars or containing : \ / ? * [ ]
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName.replace(/[:\\/?*[\]]/g, '-').slice(0, 31));
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;
// Dates stored as a timestamp carry a midnight time that means "no time", not "00:00". Printing
// it would turn a scheduled *day* into what reads as an early-morning appointment.
const ISO_MIDNIGHT = /[T ]00:00(:00)?(\.0+)?(Z|[+-]\d{2}:?\d{2})?$/;

/**
 * Makes a table's raw rows fit to read in a spreadsheet.
 *
 * A grid exported without a hand-built column set ships its row objects verbatim, so the sheet
 * carries the primary keys the grid never shows and the raw ISO timestamps it formats on
 * screen — a reader opens on a column of `C3377AB2-17D5-496E-…` beside `2026-08-05T10:30:00`.
 *
 * Two passes:
 *  - drop a column only when *every* non-empty value under it is a GUID, so anything that
 *    merely looks technical (a camp code, a UHID, a registration number) is always kept;
 *  - render ISO dates and timestamps the way the grid does, in IST.
 */
export function toReadableExportRows<T extends Record<string, unknown>>(rows: T[]): Record<string, unknown>[] {
  if (rows.length === 0) return rows;

  const guidKeys = new Set(
    Object.keys(rows[0]).filter((key) => {
      const values = rows.map((row) => row[key]).filter((v) => v !== null && v !== undefined && v !== '');
      return values.length > 0 && values.every((v) => typeof v === 'string' && GUID_PATTERN.test(v));
    }),
  );

  return rows.map((row) =>
    Object.fromEntries(
      Object.entries(row)
        .filter(([key]) => !guidKeys.has(key))
        .map(([key, value]) => {
          if (typeof value !== 'string') return [key, value];
          if (ISO_DATE_ONLY.test(value)) return [key, formatDate(value)];
          if (ISO_DATE_TIME.test(value)) {
            return [key, ISO_MIDNIGHT.test(value) ? formatDate(value) : formatDateTimeIST(value)];
          }
          return [key, value];
        }),
    ),
  );
}

/**
 * How many villages a batch's beneficiaries live in.
 *
 * Two fields carry this and they disagree: the list query returns `villageCount` with
 * `villagesIncluded` null, while the detail endpoint returns the full `villagesIncluded` array
 * but leaves `villageCount` at 0. Prefer whichever actually holds data — a `??` chain reports
 * the 0 as the answer, because 0 is not null.
 */
export function countBatchVillages(batch: { villageCount?: number; villagesIncluded?: string[] | null }): number {
  const named = batch.villagesIncluded?.length ?? 0;
  return named > 0 ? named : batch.villageCount ?? 0;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function maskAadhaar(value: string | null | undefined): string {
  if (!value || value.length < 4) return '--';
  const last4 = value.slice(-4);
  return `XXXX-XXXX-${last4}`;
}

/**
 * Returns the address fields to display for a beneficiary.
 * Always uses the present address (stored in PresentAddressId).
 */
export function getDisplayAddress(b: Beneficiary) {
  const fields = getAddressOnFileFields(b);
  return {
    district: fields.district,
    mandal: fields.mandal,
    village: fields.village,
    pincode: fields.pincode,
    address: fields.area,
    doorNo: fields.doorNo,
    streetName: fields.streetName,
    area: fields.area,
  };
}

/** Normalized present-address fields for readonly "Address on File" display across call center screens. */
function normalizePincodeDisplay(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  return /^\d{6}$/.test(trimmed) ? trimmed : '--';
}

export function getAddressOnFileFields(b: Beneficiary) {
  const pa = b.presentAddress;
  const fallbackArea = b.address?.trim().replace(/^[,\s]+$/, '') ?? '';
  const pincodeRaw = pa?.pincode?.trim() || b.pincode?.trim() || '';

  return {
    doorNo: pa?.doorNo?.trim() || '--',
    pincode: normalizePincodeDisplay(pincodeRaw),
    district: pa?.district?.trim() || b.district?.trim() || '--',
    mandal: pa?.mandal?.trim() || b.mandal?.trim() || '--',
    village: pa?.village?.trim() || b.village?.trim() || '--',
    streetName: pa?.streetName?.trim() || '--',
    area: pa?.area?.trim() || fallbackArea || '--',
  };
}

/**
 * Runs async tasks in concurrent batches to avoid slow sequential loops.
 * Calls onProgress after each batch completes so the UI can update.
 * Returns a Map keyed by original item index → result.
 */
export async function runBatched<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>,
  onProgress: (done: number, total: number, current: string) => void,
  getItemLabel: (item: T) => string,
): Promise<Map<number, R>> {
  const results = new Map<number, R>();
  let done = 0;
  for (let i = 0; i < items.length; i += concurrency) {
    const batch = items.slice(i, i + concurrency);
    const batchResults = await Promise.all(
      batch.map(async (item, j) => {
        const result = await fn(item);
        return { index: i + j, result };
      }),
    );
    for (const { index, result } of batchResults) {
      results.set(index, result);
      done++;
    }
    onProgress(done, items.length, getItemLabel(items[Math.min(i + concurrency - 1, items.length - 1)]));
  }
  return results;
}
